import { A, useLocation, useNavigate } from '@solidjs/router';
import { type Component, createEffect, createSignal, type JSX, onMount, Show } from 'solid-js';
import { AssistantProvider, AssistantSidebar, AssistantToggle, useAssistant } from './components/AIAssistant';
import { initAuth, loading, signOut, user } from './lib/supabase';

interface AppProps {
  children?: JSX.Element;
}

// Section label component for sidebar grouping
interface NavSectionProps {
  label: string;
  class?: string;
}

const NavSection: Component<NavSectionProps> = (props) => (
  <div class={`px-3 py-2 ${props.class || ''}`}>
    <span class="text-xs font-semibold uppercase tracking-wider text-gray-500">{props.label}</span>
  </div>
);

interface NavItemProps {
  href: string;
  active: boolean;
  icon:
    | 'dashboard'
    | 'projects'
    | 'executions'
    | 'skills'
    | 'artifacts'
    | 'interactions'
    | 'marketplace'
    | 'integrations'
    | 'vault'
    | 'apikeys'
    | 'groups'
    | 'settings';
  children: string;
}

const NavItem: Component<NavItemProps> = (props) => {
  const icons = {
    dashboard: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
        />
      </svg>
    ),
    projects: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
        />
      </svg>
    ),
    executions: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
        />
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
    ),
    skills: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
        />
      </svg>
    ),
    artifacts: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
        />
      </svg>
    ),
    interactions: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
        />
      </svg>
    ),
    marketplace: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
        />
      </svg>
    ),
    integrations: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z"
        />
      </svg>
    ),
    vault: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      </svg>
    ),
    apikeys: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
        />
      </svg>
    ),
    groups: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
        />
      </svg>
    ),
    settings: (
      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
        />
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  };

  return (
    <li class="mb-1">
      <A
        href={props.href}
        class={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all no-underline ${
          props.active
            ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg shadow-purple-500/20'
            : 'text-gray-300 hover:bg-purple-500/10 hover:text-white'
        }`}
      >
        {icons[props.icon]}
        {props.children}
      </A>
    </li>
  );
};

const App: Component<AppProps> = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userMenuOpen, setUserMenuOpen] = createSignal(false);

  // Initialize auth on mount
  onMount(() => {
    initAuth();
  });

  // Check if current route is a public route (login, callback, reset-password)
  const isPublicRoute = () => {
    const path = location.pathname;
    return (
      path.includes('/login') || path.includes('/auth/callback') || path.includes('/reset-password')
    );
  };

  // Handle redirects based on auth state (reactive)
  createEffect(() => {
    if (!loading()) {
      if (!user() && !isPublicRoute()) {
        // Not authenticated and on protected route -> go to login
        navigate('/login', { replace: true });
      } else if (user() && location.pathname === '/login') {
        // Authenticated and on login page -> go to dashboard
        navigate('/', { replace: true });
      }
    }
  });

  const basePath = import.meta.env.PROD ? '/portal' : '';
  const isActive = (path: string) => {
    const fullPath = basePath + path;
    if (path === '/') {
      return location.pathname === fullPath || location.pathname === basePath;
    }
    return location.pathname.startsWith(fullPath);
  };

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    navigate('/login');
  };

  // Get user display name or email
  const getUserDisplay = () => {
    const u = user();
    if (!u) return '';
    return u.user_metadata?.full_name || u.user_metadata?.name || u.email || 'User';
  };

  const getUserInitial = () => {
    const display = getUserDisplay();
    return display.charAt(0).toUpperCase();
  };

  // Loading spinner component
  const LoadingSpinner = () => (
    <div class="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 to-gray-950">
      <div class="text-center">
        <svg class="animate-spin w-8 h-8 text-purple-500 mx-auto" fill="none" viewBox="0 0 24 24">
          <circle
            class="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            stroke-width="4"
          />
          <path
            class="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p class="text-gray-400 mt-3">Loading...</p>
      </div>
    </div>
  );

  // Main authenticated layout
  const AuthenticatedLayout = () => (
    <div class="flex min-h-screen bg-gradient-to-br from-gray-900 to-gray-950">
      {/* Sidebar */}
      <nav class="w-60 bg-gray-800/50 p-5 border-r border-gray-700 flex flex-col">
        {/* Logo/Title - links to landing */}
        <a href="https://aigentswork.com" class="mb-6 block hover:opacity-80 transition-opacity no-underline">
          <div class="flex items-center gap-2">
            <div class="w-8 h-8 bg-gradient-to-br from-purple-500 to-pink-600 rounded-lg flex items-center justify-center">
              <span class="text-white font-bold text-sm">AW</span>
            </div>
            <div>
              <h2 class="text-lg font-semibold m-0 text-white">Aigents Work</h2>
              <span class="text-xs text-gray-400">Portal</span>
            </div>
          </div>
        </a>

        <div class="flex-1 flex flex-col">
          {/* Workspace Section */}
          <NavSection label="Workspace" />
          <ul class="list-none p-0 m-0">
            <NavItem href="/" active={isActive('/')} icon="dashboard">
              Dashboard
            </NavItem>
            <NavItem href="/projects" active={isActive('/projects')} icon="projects">
              Projects
            </NavItem>
            <NavItem href="/executions" active={isActive('/executions')} icon="executions">
              Executions
            </NavItem>
            <NavItem href="/skills" active={isActive('/skills')} icon="skills">
              Skills
            </NavItem>
            <NavItem href="/artifacts" active={isActive('/artifacts')} icon="artifacts">
              Artifacts
            </NavItem>
            <NavItem href="/interactions" active={isActive('/interactions')} icon="interactions">
              Interactions
            </NavItem>
          </ul>

          {/* Marketplace - links to landing page */}
          <div class="mt-4">
            <ul class="list-none p-0 m-0">
              <li class="mb-1">
                <a
                  href="https://aigentswork.com/marketplace"
                  class="flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-gray-300 hover:bg-purple-500/10 hover:text-white no-underline"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
                    />
                  </svg>
                  Marketplace
                </a>
              </li>
            </ul>
          </div>

          {/* Settings Section */}
          <NavSection label="Settings" class="mt-6" />
          <ul class="list-none p-0 m-0">
            <NavItem href="/integrations" active={isActive('/integrations')} icon="integrations">
              Integrations
            </NavItem>
            <NavItem href="/vault" active={isActive('/vault')} icon="vault">
              Vault
            </NavItem>
            <NavItem href="/api-keys" active={isActive('/api-keys')} icon="apikeys">
              API Keys
            </NavItem>
            <NavItem href="/groups" active={isActive('/groups')} icon="groups">
              Groups
            </NavItem>
            <NavItem href="/settings" active={isActive('/settings')} icon="settings">
              Settings
            </NavItem>
          </ul>
        </div>

        {/* Version Info */}
        <div class="mt-auto pt-4 border-t border-gray-700 mb-3">
          <div
            class="px-3 py-1 text-xs text-gray-500 hover:text-gray-300 cursor-pointer"
            title={`Build: ${__BUILD_COMMIT__}\nTime: ${__BUILD_TIME__}`}
          >
            v{__APP_VERSION__} ({__BUILD_COMMIT__})
          </div>
        </div>

        {/* User Menu */}
        <div class="pt-2">
          <div class="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen())}
              class="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-gray-300 hover:bg-purple-500/10 hover:text-white transition-all"
            >
              <div class="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white font-medium text-sm">
                {getUserInitial()}
              </div>
              <div class="flex-1 text-left overflow-hidden">
                <div class="text-sm font-medium truncate">{getUserDisplay()}</div>
                <div class="text-xs text-gray-500 truncate">{user()?.email}</div>
              </div>
              <svg
                class={`w-4 h-4 text-gray-500 transition-transform ${
                  userMenuOpen() ? 'rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>

            {/* Dropdown Menu */}
            <Show when={userMenuOpen()}>
              <div class="absolute bottom-full left-0 right-0 mb-2 bg-gray-800 border border-gray-700 rounded-xl shadow-xl overflow-hidden">
                <A
                  href="/settings"
                  class="flex items-center gap-3 px-4 py-3 text-gray-300 hover:bg-gray-700/50 transition-colors"
                  onClick={() => setUserMenuOpen(false)}
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </A>
                <button
                  onClick={handleSignOut}
                  class="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 transition-colors"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Sign Out
                </button>
              </div>
            </Show>
          </div>
        </div>
      </nav>

      {/* Main Content Area - shrinks when assistant is open */}
      <MainContentWithAssistant>{props.children}</MainContentWithAssistant>
    </div>
  );

  // Wrapper component to access assistant state
  const MainContentWithAssistant: Component<{ children?: JSX.Element }> = (contentProps) => {
    const { state } = useAssistant();

    return (
      <>
        {/* Main Content */}
        <div
          class="flex-1 flex flex-col min-w-0 transition-all duration-300"
          style={{ 'margin-right': state().isOpen ? '400px' : '0' }}
        >
          {/* Top bar with AI Assistant toggle */}
          <header class="flex items-center justify-end px-6 py-3 border-b border-gray-800/50 bg-gray-900/30">
            <AssistantToggle />
          </header>
          <main class="flex-1 p-6 overflow-auto">{contentProps.children}</main>
        </div>

        {/* AI Assistant Sidebar - fixed but content shifts */}
        <AssistantSidebar />
      </>
    );
  };

  return (
    <AssistantProvider>
      <Show when={!loading()} fallback={<LoadingSpinner />}>
        <Show
          when={isPublicRoute()}
          fallback={
            <Show when={user()} fallback={<LoadingSpinner />}>
              <AuthenticatedLayout />
            </Show>
          }
        >
          {props.children}
        </Show>
      </Show>
    </AssistantProvider>
  );
};

export default App;
